// Stan konstrukcji: blueprint, zaznaczenie i historia cofnij/ponow.
// Jedno miejsce, w ktorym zmienia sie konstrukcja - reszta modulow tylko czyta.

import { cloneBlueprint } from '../domain/blueprint';
import { getDefinition, hasDefinition } from '../domain/catalog';
import { blueprintCost } from '../domain/costs';
import { remainingFor, type PartLimits } from '../domain/limits';
import type {
  Connection,
  PartInstance,
  RobotBlueprint,
  Rotation,
} from '../domain/types';
import { validateAll, validateBuilding, validateForTrial, type ValidationIssue } from '../domain/validate';
import { reconnectPart } from '../editor/snapping';

const MAX_HISTORY = 50;

export class BlueprintStore {
  private blueprint: RobotBlueprint;
  private startBlueprint: RobotBlueprint;
  private undoStack: RobotBlueprint[] = [];
  private redoStack: RobotBlueprint[] = [];
  private selectedId: string | null = null;
  private readonly listeners = new Set<() => void>();
  /** Limit kosztu konstrukcji dla biezacej planszy (undefined = bez limitu). */
  private budgetPln: number | undefined;
  /** Limity klockow dla biezacej planszy (undefined = bez ograniczen). */
  private limits: PartLimits | undefined;

  constructor(initial: RobotBlueprint) {
    this.startBlueprint = cloneBlueprint(initial);
    this.blueprint = cloneBlueprint(initial);
  }

  get current(): RobotBlueprint {
    return this.blueprint;
  }

  get selectedPartId(): string | null {
    return this.selectedId;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Ustawia limit kosztu konstrukcji (zmienia sie przy zmianie planszy). */
  setBudget(budgetPln: number | undefined): void {
    if (this.budgetPln === budgetPln) return;
    this.budgetPln = budgetPln;
    this.notify();
  }

  /**
   * Ile budzetu zostalo na nowe klocki (undefined = plansza bez limitu).
   * Kontroli trzyma sie addPart; walidacja OVER_BUDGET zostaje jako siatka
   * bezpieczenstwa dla konstrukcji wczytanych z zapisu.
   */
  get remainingBudget(): number | undefined {
    if (this.budgetPln === undefined) return undefined;
    return this.budgetPln - blueprintCost(this.blueprint);
  }

  /** Limity klockow dla biezacej planszy (undefined = bez ograniczen). */
  get partLimits(): PartLimits | undefined {
    return this.limits;
  }

  /** Ustawia limity klockow (zmieniaja sie przy zmianie trasy). */
  setLimits(limits: PartLimits | undefined): void {
    if (this.limits === limits) return;
    this.limits = limits;
    this.notify();
  }

  /** Ile jeszcze wolno dodac klockow tego typu (undefined = bez limitu). */
  remainingForType(typeId: string): number | undefined {
    if (!hasDefinition(typeId)) return undefined;
    return remainingFor(getDefinition(typeId), this.blueprint, this.limits);
  }

  /** Bledy blokujace Test (struktura + kompletnosc konstrukcji + budzet + limity). */
  issues(): ValidationIssue[] {
    return validateAll(this.blueprint, this.budgetPln, this.limits);
  }

  /**
   * Bledy blokujace WYJSCIE NA TRASE: struktura + budzet + limity klockow. Braki
   * gotowosci (np. brak akumulatora albo napedu) NIE blokuja - taka konstrukcja
   * co najwyzej nie ruszy z miejsca.
   */
  trialIssues(): ValidationIssue[] {
    return validateForTrial(this.blueprint, this.budgetPln, this.limits);
  }

  /** Bledy istotne w trakcie montazu (bez wymogu kompletnosci). */
  buildingIssues(): ValidationIssue[] {
    return validateBuilding(this.blueprint);
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  private commit(next: RobotBlueprint): void {
    this.undoStack.push(cloneBlueprint(this.blueprint));
    if (this.undoStack.length > MAX_HISTORY) this.undoStack.shift();
    this.redoStack = [];
    this.blueprint = next;
    if (this.selectedId && !next.parts.some((part) => part.id === this.selectedId)) {
      this.selectedId = null;
    }
    this.notify();
  }

  select(partId: string | null): void {
    if (this.selectedId === partId) return;
    this.selectedId = partId;
    this.notify();
  }

  /**
   * Dodaje klocek, o ile zmiesci sie w budzecie planszy. Zwraca false, gdy
   * dodanie zostalo odrzucone (koszt przekroczylby limit) - konstrukcja zostaje
   * wtedy bez zmian.
   *
   * `connection === null` oznacza PIERWSZY klocek konstrukcji: nie ma jeszcze
   * mocowania, do ktorego mozna go przyczepic, wiec stawiamy go swobodnie jako korzen.
   */
  addPart(instance: PartInstance, connection: Connection | null): boolean {
    const next: RobotBlueprint = {
      ...this.blueprint,
      parts: [...this.blueprint.parts, { ...instance }],
      connections: connection
        ? [...this.blueprint.connections, { a: { ...connection.a }, b: { ...connection.b } }]
        : [...this.blueprint.connections],
    };

    // Twardy limit: nie da sie dokladac klockow, gdy zabraknie budzetu...
    if (this.budgetPln !== undefined && blueprintCost(next) > this.budgetPln) return false;
    // ...ani gdy wyczerpal sie limit klockow tej trasy (np. najwyzej dwa kola).
    const remaining = this.remainingForType(instance.typeId);
    if (remaining !== undefined && remaining <= 0) return false;

    this.selectedId = instance.id;
    this.commit(next);
    return true;
  }

  removePart(partId: string): void {
    this.commit({
      ...this.blueprint,
      parts: this.blueprint.parts.filter((part) => part.id !== partId),
      connections: this.blueprint.connections.filter(
        (conn) => conn.a.partId !== partId && conn.b.partId !== partId,
      ),
    });
  }

  /**
   * Przeniesienie postawionego klocka (przeciaganie). Jak przy obrocie:
   * zdejmujemy jego polaczenia i odtwarzamy je w nowym miejscu. Dodatkowo
   * wymagamy, by po przeniesieniu konstrukcja nadal byla poprawna (walidacja
   * budowania) - klocek nie moze rozspojnic korpusu ani wejsc w inny klocek.
   *
   * `allowFree` dopuszcza swobodny ruch, gdy przenoszony klocek jest JEDYNA
   * czescia konstrukcji (nie ma mocowania, do ktorego moglby sie przyczepic -
   * to ten sam wyjatek co przy stawianiu pierwszego klocka na pustym warsztacie).
   */
  movePart(
    partId: string,
    transform: { gridX: number; gridY: number; rotation: Rotation },
    options: { allowFree?: boolean } = {},
  ): boolean {
    const part = this.blueprint.parts.find((entry) => entry.id === partId);
    if (!part) return false;
    if (
      part.gridX === transform.gridX &&
      part.gridY === transform.gridY &&
      part.rotation === transform.rotation
    ) {
      return false;
    }

    const detached: RobotBlueprint = {
      ...this.blueprint,
      parts: this.blueprint.parts.map((entry) =>
        entry.id === partId ? { ...entry, ...transform } : entry,
      ),
      connections: this.blueprint.connections.filter(
        (conn) => conn.a.partId !== partId && conn.b.partId !== partId,
      ),
    };

    const reconnected = reconnectPart(detached, partId);
    if (!reconnected) {
      // Samotny klocek nie ma sie do czego przyczepic - swobodny ruch jest ok.
      if (options.allowFree && detached.parts.length === 1) {
        this.commit(detached);
        return true;
      }
      return false;
    }

    if (validateBuilding(reconnected).length > 0) return false;
    this.commit(reconnected);
    return true;
  }

  /**
   * Obrot postawionego klocka. Najpierw zdejmujemy jego polaczenia (sa
   * geometryczne), potem probujemy je odtworzyc w nowym obrocie. Gdy klocek
   * zostalby odlaczony, obrot jest odrzucany.
   */
  rotatePart(partId: string, rotation: Rotation): boolean {
    const part = this.blueprint.parts.find((entry) => entry.id === partId);
    if (!part || part.rotation === rotation) return false;

    const detached: RobotBlueprint = {
      ...this.blueprint,
      parts: this.blueprint.parts.map((entry) =>
        entry.id === partId ? { ...entry, rotation } : entry,
      ),
      connections: this.blueprint.connections.filter(
        (conn) => conn.a.partId !== partId && conn.b.partId !== partId,
      ),
    };

    const reconnected = reconnectPart(detached, partId);
    if (!reconnected) return false;
    this.commit(reconnected);
    return true;
  }

  undo(): boolean {
    const previous = this.undoStack.pop();
    if (!previous) return false;
    this.redoStack.push(cloneBlueprint(this.blueprint));
    this.blueprint = previous;
    this.notify();
    return true;
  }

  redo(): boolean {
    const next = this.redoStack.pop();
    if (!next) return false;
    this.undoStack.push(cloneBlueprint(this.blueprint));
    this.blueprint = next;
    this.notify();
    return true;
  }

  /** Wraca do konstrukcji, od ktorej zaczeto (przycisk "Przywroc konstrukcje startowa"). */
  resetToStart(): void {
    this.commit(cloneBlueprint(this.startBlueprint));
    this.undoStack = [];
    this.redoStack = [];
    this.notify();
  }

  /**
   * Wczytuje inna konstrukcje jako punkt wyjscia (lista przykladow / garaz).
   * Aktualizuje takze konstrukcje startowa, wiec "Przywroc konstrukcje startowa"
   * cofa do TEGO wlasnie robota, a nie do pierwszego z listy.
   */
  load(blueprint: RobotBlueprint): void {
    this.undoStack = [];
    this.redoStack = [];
    this.selectedId = null;
    this.blueprint = cloneBlueprint(blueprint);
    this.startBlueprint = cloneBlueprint(blueprint);
    this.notify();
  }
}